import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Sparkles, Users, Radio, ShoppingBag, Bot, Calendar } from 'lucide-react';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JadePage = () => {
  const [form, setForm] = useState({ name: '', email: '', gender: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(null);
  const [activeFeature, setActiveFeature] = useState(0);
  const featuresRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/waitlist/count`)
      .then(r => r.json())
      .then(d => setWaitlistCount(d.count))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Please enter your name'); return; }
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError('Please enter a valid email address'); return; }
    if (!form.gender) { setError('Please select an option'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail?.[0]?.msg || 'Something went wrong');
      }
      setSubmitted(true);
      setWaitlistCount(prev => (prev || 0) + 1);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const scrollFeature = (idx) => {
    setActiveFeature(idx);
    if (featuresRef.current) {
      const cards = featuresRef.current.children;
      if (cards[idx]) cards[idx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  };

  return (
    <div className="bg-black text-white min-h-screen" data-testid="jade-page">
      <SEO
        title="JADE — Your Identity, Worn | Join the Waitlist"
        description="JADE makes clothing your identity. Buy a drop, get your NFC identity, join personality-based clans, attend events, and connect with Jaders worldwide."
        canonical="https://www.owlworklabs.com/jade"
      />

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 pt-3 sm:pt-4" data-testid="jade-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-xs font-medium text-white/50 hover:text-white transition-all px-3 py-2 rounded-full border border-white/10 hover:border-white/30" data-testid="jade-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">OwlWorkLabs</span>
          </Link>
          <span className="text-[11px] tracking-[0.3em] text-white/40 uppercase">Jade</span>
        </div>
      </header>

      {/* ==================== */}
      {/* HERO                  */}
      {/* ==================== */}
      <section className="relative min-h-screen flex flex-col justify-center items-center px-6 overflow-hidden" data-testid="jade-hero">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[150px]" />
        </div>

        <div className="relative z-10 text-center max-w-3xl pt-20">
          <h1 className="text-6xl sm:text-8xl md:text-[9rem] font-black leading-[0.85] tracking-tight uppercase">
            Jade
            <br />
            <span className="text-white/30">is worn.</span>
          </h1>
          <p className="text-sm sm:text-base text-white/50 mt-6 sm:mt-8 max-w-lg mx-auto leading-relaxed">
            Your clothing is your identity. Wear it. Connect through it. Let the world know who you are — without saying a word.
          </p>

          <div className="mt-10 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/30 bg-emerald-500/5">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs text-emerald-400 tracking-wide">One drop. Full access. No extra fees.</span>
          </div>

          <div className="mt-8">
            <a href="#waitlist" className="inline-block px-8 py-4 bg-white text-black font-bold text-sm tracking-[0.15em] uppercase rounded-full hover:bg-white/90 transition-all hover:scale-105" data-testid="jade-hero-cta">
              Join the Waitlist
            </a>
          </div>

          {waitlistCount > 0 && (
            <p className="text-xs text-white/20 mt-4 tracking-widest uppercase">
              {waitlistCount} people waiting
            </p>
          )}
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center text-white/20">
          <div className="w-px h-10 bg-gradient-to-b from-transparent to-white/20" />
        </div>
      </section>

      {/* ======================== */}
      {/* WHAT IS JADE             */}
      {/* ======================== */}
      <section className="py-12 sm:py-28 px-6 relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-[10px] tracking-[0.4em] text-white/30 uppercase block">More than clothing</span>
          <h2 className="text-3xl sm:text-5xl font-black mt-3 uppercase tracking-tight">
            Wear it. Be it.
          </h2>
          <p className="text-sm sm:text-base text-white/40 mt-6 max-w-2xl mx-auto leading-relaxed">
            JADE is making clothing more than just a style statement. Every piece you wear puts your identity out there without even trying. Buy any drop and you get everything — your NFC identity card, access to personality-based clans, curated events, and a world of Jaders to connect with. No memberships. No hidden fees. Just the cloth.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12">
            <div className="p-4 rounded-xl border border-white/5 bg-white/[0.02]">
              <ShoppingBag className="w-5 h-5 text-emerald-400 mx-auto" />
              <p className="text-xs text-white/50 mt-3">Exclusive drops</p>
            </div>
            <div className="p-4 rounded-xl border border-white/5 bg-white/[0.02]">
              <Users className="w-5 h-5 text-emerald-400 mx-auto" />
              <p className="text-xs text-white/50 mt-3">Personality clans</p>
            </div>
            <div className="p-4 rounded-xl border border-white/5 bg-white/[0.02]">
              <Calendar className="w-5 h-5 text-emerald-400 mx-auto" />
              <p className="text-xs text-white/50 mt-3">Events for everyone</p>
            </div>
            <div className="p-4 rounded-xl border border-white/5 bg-white/[0.02]">
              <Radio className="w-5 h-5 text-emerald-400 mx-auto" />
              <p className="text-xs text-white/50 mt-3">NFC connections</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========================== */}
      {/* THE DROPS                   */}
      {/* ========================== */}
      <section className="py-12 sm:py-28 px-6 relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="flex-1 text-center lg:text-left">
              <span className="text-[10px] tracking-[0.4em] text-white/30 uppercase block">The Drops</span>
              <h2 className="text-3xl sm:text-4xl font-black mt-3 uppercase tracking-tight">
                Every piece tells<br />a different story
              </h2>
              <p className="text-sm text-white/40 mt-5 leading-relaxed max-w-lg mx-auto lg:mx-0">
                Each JADE drop is a unique collaboration — with artists, popular clothing brands, football clubs, and cultural icons. Every piece carries a different style and aura. Find the one that speaks to you.
              </p>
              <p className="text-sm text-white/40 mt-4 leading-relaxed max-w-lg mx-auto lg:mx-0">
                And every piece is identified through its drop. No two collections are the same. When it sells out, it's gone forever.
              </p>
            </div>
            <div className="w-56 sm:w-64 rounded-[2.5rem] overflow-hidden border-[3px] border-white/10 shadow-2xl shadow-emerald-500/5 flex-shrink-0">
              <img src="/jade-shop.jpg" alt="JADE Drops" loading="lazy" className="w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ========================== */}
      {/* YOUR IDENTITY               */}
      {/* ========================== */}
      <section className="py-12 sm:py-28 px-6 relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12 lg:gap-16">
            <div className="flex-1 text-center lg:text-left">
              <span className="text-[10px] tracking-[0.4em] text-white/30 uppercase block">Your Identity</span>
              <h2 className="text-3xl sm:text-4xl font-black mt-3 uppercase tracking-tight">
                Clans built around<br />who you really are
              </h2>
              <p className="text-sm text-white/40 mt-5 leading-relaxed max-w-lg mx-auto lg:mx-0">
                When you join JADE, you get a unique NFC identity card and get placed into clans based on your 16PF personality profile and star sign. Meet people who get you — or explore different clans and make friends across every group.
              </p>
              <p className="text-sm text-white/40 mt-4 leading-relaxed max-w-lg mx-auto lg:mx-0">
                No additional community fees. Ever. Your drop is your ticket in.
              </p>
            </div>
            <div className="w-56 sm:w-64 rounded-[2.5rem] overflow-hidden border-[3px] border-white/10 shadow-2xl shadow-emerald-500/5 flex-shrink-0">
              <img src="/jade-profile.jpg" alt="JADE Profile & Clans" loading="lazy" className="w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ========================== */}
      {/* APP FEATURES — Carousel     */}
      {/* ========================== */}
      <section className="py-12 sm:py-28 px-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        <div className="max-w-5xl mx-auto">
          <span className="text-[10px] tracking-[0.4em] text-white/30 uppercase block text-center">Inside the app</span>
          <h2 className="text-3xl sm:text-5xl font-black text-center mt-3 uppercase tracking-tight">
            Everything in One Place
          </h2>
          <p className="text-sm text-white/40 text-center mt-4 max-w-lg mx-auto">
            Connect, discover, and express yourself — all from one app.
          </p>

          {/* Feature pills */}
          <div className="flex justify-center gap-2 mt-10 flex-wrap">
            <button onClick={() => scrollFeature(0)} className={`px-4 py-2 rounded-full text-xs tracking-widest uppercase transition-all border ${activeFeature === 0 ? 'bg-white text-black border-white' : 'bg-transparent text-white/40 border-white/10 hover:border-white/30'}`} data-testid="jade-feature-radar">Connect</button>
            <button onClick={() => scrollFeature(1)} className={`px-4 py-2 rounded-full text-xs tracking-widest uppercase transition-all border ${activeFeature === 1 ? 'bg-white text-black border-white' : 'bg-transparent text-white/40 border-white/10 hover:border-white/30'}`} data-testid="jade-feature-bot">The Bot</button>
            <button onClick={() => scrollFeature(2)} className={`px-4 py-2 rounded-full text-xs tracking-widest uppercase transition-all border ${activeFeature === 2 ? 'bg-white text-black border-white' : 'bg-transparent text-white/40 border-white/10 hover:border-white/30'}`} data-testid="jade-feature-events">Events</button>
          </div>

          {/* Feature cards */}
          <div
            ref={featuresRef}
            className="flex overflow-x-auto snap-x snap-mandatory gap-4 sm:gap-6 mt-8 pb-4 -mx-6 px-6"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {/* Connect / Radar */}
            <div className="w-[82vw] max-w-[82vw] sm:w-[60vw] sm:max-w-[60vw] lg:w-[45vw] lg:max-w-[480px] snap-center flex-shrink-0">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden hover:border-white/20 transition-colors">
                <div className="relative overflow-hidden bg-neutral-900 flex items-center justify-center py-6 px-4 sm:px-8">
                  <div className="flex gap-3">
                    <div className="w-36 sm:w-44 rounded-[1.8rem] overflow-hidden border-[2px] border-white/10 shadow-xl">
                      <img src="/jade-radar.jpg" alt="JADE Radar" loading="lazy" className="w-full object-cover" />
                    </div>
                    <div className="w-36 sm:w-44 rounded-[1.8rem] overflow-hidden border-[2px] border-white/10 shadow-xl hidden sm:block">
                      <img src="/jade-nfc.jpg" alt="JADE NFC Tap" loading="lazy" className="w-full object-cover" />
                    </div>
                  </div>
                </div>
                <div className="p-4 sm:p-5">
                  <h3 className="text-base sm:text-lg font-bold uppercase tracking-wide">Connect Your Way</h3>
                  <p className="text-xs text-white/50 mt-2 leading-relaxed break-words">
                    Wear your piece, carry your NFC card, broadcast to nearby Jaders. Tap to connect — privacy first, you decide who sees you.
                  </p>
                </div>
              </div>
            </div>

            {/* The Bot */}
            <div className="w-[82vw] max-w-[82vw] sm:w-[60vw] sm:max-w-[60vw] lg:w-[45vw] lg:max-w-[480px] snap-center flex-shrink-0">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden hover:border-white/20 transition-colors">
                <div className="relative overflow-hidden bg-neutral-900 flex items-center justify-center py-6 px-4 sm:px-8">
                  <div className="w-44 sm:w-56 rounded-[2rem] overflow-hidden border-[3px] border-white/10 shadow-2xl shadow-emerald-500/5">
                    <img src="/jade-bot.jpg" alt="JADE Bot — Stylist, Oracle, Wingman" loading="lazy" className="w-full object-cover" />
                  </div>
                </div>
                <div className="p-4 sm:p-5">
                  <h3 className="text-base sm:text-lg font-bold uppercase tracking-wide">Your Wingman</h3>
                  <p className="text-xs text-white/50 mt-2 leading-relaxed break-words">
                    Stylist, oracle, lifestyle coach — tuned to your 16PF profile and star sign. Helps you be more of what you already are.
                  </p>
                </div>
              </div>
            </div>

            {/* Events */}
            <div className="w-[82vw] max-w-[82vw] sm:w-[60vw] sm:max-w-[60vw] lg:w-[45vw] lg:max-w-[480px] snap-center flex-shrink-0">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden hover:border-white/20 transition-colors">
                <div className="relative overflow-hidden bg-neutral-900 flex items-center justify-center py-6 px-4 sm:px-8">
                  <div className="w-44 sm:w-56 rounded-[2rem] overflow-hidden border-[3px] border-white/10 shadow-2xl shadow-emerald-500/5">
                    <img src="/jade-events.jpg" alt="JADE Events — The Hub" loading="lazy" className="w-full object-cover" />
                  </div>
                </div>
                <div className="p-4 sm:p-5">
                  <h3 className="text-base sm:text-lg font-bold uppercase tracking-wide">Events for Everyone</h3>
                  <p className="text-xs text-white/50 mt-2 leading-relaxed break-words">
                    Intimate gatherings to city-wide meetups — themed events shaped by clan polls and votes. Your voice, your scene.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================== */}
      {/* HOW IT WORKS                */}
      {/* ========================== */}
      <section className="py-12 sm:py-28 px-6 relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        <div className="max-w-4xl mx-auto text-center">
          <span className="text-[10px] tracking-[0.4em] text-white/30 uppercase block">Simple</span>
          <h2 className="text-3xl sm:text-5xl font-black mt-3 uppercase tracking-tight">
            How It Works
          </h2>

          <div className="grid sm:grid-cols-3 gap-8 mt-14">
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full border border-emerald-500/20 bg-emerald-500/5 flex items-center justify-center text-2xl font-black text-emerald-400/50">1</div>
              <h3 className="text-sm font-bold uppercase tracking-widest mt-5">Join the Waitlist</h3>
              <p className="text-xs text-white/30 mt-2 leading-relaxed">Sign up. Be first to know when drops go live.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full border border-emerald-500/20 bg-emerald-500/5 flex items-center justify-center text-2xl font-black text-emerald-400/50">2</div>
              <h3 className="text-sm font-bold uppercase tracking-widest mt-5">Get Your Drop</h3>
              <p className="text-xs text-white/30 mt-2 leading-relaxed">Pick the piece that speaks to you. That's your ticket in — no extra fees, no subscriptions.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full border border-emerald-500/20 bg-emerald-500/5 flex items-center justify-center text-2xl font-black text-emerald-400/50">3</div>
              <h3 className="text-sm font-bold uppercase tracking-widest mt-5">Enter the World</h3>
              <p className="text-xs text-white/30 mt-2 leading-relaxed">Get your NFC card, join your clan, attend events, connect with Jaders. The whole world opens up.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========================== */}
      {/* WAITLIST FORM               */}
      {/* ========================== */}
      <section id="waitlist" className="py-12 sm:py-28 px-6 relative overflow-hidden" data-testid="jade-waitlist-section">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-500/5 rounded-full blur-[150px]" />
        </div>

        <div className="max-w-md mx-auto relative z-10 text-center">
          <Sparkles className="w-6 h-6 text-emerald-400/50 mx-auto" />
          <h2 className="text-3xl sm:text-5xl font-black mt-4 uppercase tracking-tight">
            Be First In
          </h2>
          <p className="text-sm text-white/40 mt-4">
            Join the waitlist. Be the first to know when drops go live and get early access.
          </p>

          {!submitted ? (
            <form onSubmit={handleSubmit} noValidate className="mt-10 space-y-3" data-testid="jade-waitlist-form">
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder="Your name"
                aria-label="Your name"
                required
                className="w-full px-5 py-4 bg-white/[0.05] border border-white/10 rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-emerald-500/50 transition-colors"
                data-testid="jade-name-input"
              />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})}
                placeholder="Your email"
                aria-label="Your email address"
                required
                className="w-full px-5 py-4 bg-white/[0.05] border border-white/10 rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-emerald-500/50 transition-colors"
                data-testid="jade-email-input"
              />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" role="group" aria-label="Gender">
                {['Male', 'Female', 'Non-binary', 'Prefer not to say'].map((g) => (
                  <button
                    key={g}
                    type="button"
                    aria-pressed={form.gender === g.toLowerCase()}
                    onClick={() => setForm({...form, gender: g.toLowerCase()})}
                    className={`px-3 py-3 rounded-xl text-xs font-medium transition-all border ${
                      form.gender === g.toLowerCase()
                        ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300'
                        : 'bg-white/[0.03] border-white/10 text-white/40 hover:border-white/20'
                    }`}
                    data-testid={`jade-gender-${g.toLowerCase().replace(/ /g, '-')}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 px-8 py-4 bg-emerald-500 text-black font-bold text-sm tracking-[0.15em] uppercase rounded-xl hover:bg-emerald-400 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="jade-submit-btn"
              >
                {loading ? 'Joining...' : 'Join the Waitlist'}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>
              {error && <p className="text-red-400 text-xs mt-2" data-testid="jade-error">{error}</p>}
              <p className="text-[11px] text-white/25 mt-3 leading-relaxed">
                By joining, you agree to our{' '}
                <Link to="/privacy" className="underline hover:text-white/50 transition-colors">Privacy Policy</Link>.
                We'll only use your details to contact you about JADE.
              </p>
            </form>
          ) : (
            <div className="mt-10 p-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/5" data-testid="jade-success">
              <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center mx-auto">
                <Check className="w-6 h-6 text-black" />
              </div>
              <h3 className="text-lg font-bold uppercase tracking-widest mt-5">You're in, {form.name.split(' ')[0]}</h3>
              <p className="text-xs text-white/40 mt-2">
                We'll let you know as soon as the first drop goes live. Get ready.
              </p>
            </div>
          )}

          {waitlistCount > 0 && (
            <p className="text-xs text-white/15 mt-6 tracking-widest uppercase">
              {waitlistCount} already waiting
            </p>
          )}
        </div>
      </section>

      {/* ========================== */}
      {/* FOOTER                      */}
      {/* ========================== */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-black uppercase tracking-wider">Jade</span>
            <span className="text-[10px] text-white/20">by OwlWorkLabs</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-white/25">
            <Link to="/" className="hover:text-white/50 transition-colors">Home</Link>
            <a href="#waitlist" className="hover:text-white/50 transition-colors">Waitlist</a>
            <Link to="/privacy" className="hover:text-white/50 transition-colors" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/terms" className="hover:text-white/50 transition-colors" data-testid="footer-terms-link">Terms</Link>
            <span>London, UK</span>
          </div>
          <p className="text-[10px] text-white/15">&copy; {new Date().getFullYear()} OwlWorkLabs Ltd. · Company No. 16713122</p>
        </div>
      </footer>
    </div>
  );
};

export default JadePage;
